<template>
    <main>
        <SideBar
            :current-language="prismicLang"
            :clc-name="clcName"
            :highlight="sidebarHighlight"
            :dynamic-sidebar="dynamicSidebar"
            @toggle-language="toggleLanguage"
        />
        <section class="News-detail-anchor" data-component-name="News-detail">

            <!-- Loading State -->
            <div class="loading-state" v-if="isLoading">
                <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" style="text-align: center; padding: 60px 20px;">
                <p style="color: red;">{{ prismicLang === 'en-us' ? 'Error loading news:' : 'Error al cargar la noticia:' }} {{ error }}</p>
                <a :href="homeUrl" style="color: #009FDB; text-decoration: underline;">
                    {{ prismicLang === 'en-us' ? 'Back to Home' : 'Volver al Inicio' }}
                </a>
            </div>

            <!-- Content (only when data is loaded) -->
            <template v-else-if="newsData">
                <a :href="newsListUrl" class="CLCNews__back">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none" stroke="currentColor" aria-hidden="true">
                        <path d="M21 10.5H1" stroke-width="2"/>
                        <path d="M10 1.5L1 10.5L10 19.5" stroke-width="2"/>
                    </svg>
                    <span>{{ prismicLang === 'en-us' ? 'Back to News' : 'Volver a Noticias' }}</span>
                </a>
                <article class="top">
                    <figure>
                        <div class="box-data">
                            <div class="wrapper">
                                <span v-if="category" class="news-detail__cat">{{ category }}</span>
                                <h1>{{ newsTitle }}</h1>
                            </div>
                        </div>
                        <div class="box-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
                    </figure>
                </article>
                <article class="bottom">
                    <div class="head-news">
                        <p>{{ author }}</p>
                        <p v-if="publishedDate"><i>{{ prismicLang === 'en-us' ? 'Published' : 'Publicado' }}: {{ publishedDate }}</i></p>
                    </div>
                    <component
                        v-for="(slice, index) in bodySlices"
                        :key="`${slice.slice_type}-${index}`"
                        :is="getSliceComponent(slice)"
                        :slice-data="slice"
                        :prismic-lang="prismicLang"
                    />
                </article>
            </template>
        </section>
        <footer>
            <p>
                <img src="../assets/images/rebrand-2026/logo-footer.png" alt="ATT Logo" />
                <span>Corporate Responsibility</span>
            </p>
            <ul>
                <li>
                    <a href="https://www.att.com/" target="_blank" rel="noopener" alt="ATT">www.att.com</a>
                </li>
                <li>
                    <a href="https://sustainability.att.com/" target="_blank" rel="noopener" alt="Sustainability ATT">sustainability.att.com</a>
                </li>
            </ul>
        </footer>
    </main>
</template>

<script>
import SideBar from './SideBar.vue';
import RichText from './slices/RichText.vue';
import CtaButton from './slices/CtaButton.vue';
import ImageWrapper from './slices/ImageWrapper.vue';
import LeyendComponent from './slices/Leyend.vue';
import VideoWrapper from './slices/VideoWrapper.vue';
import QuoteComponent from './slices/Quote.vue';
import BannerImage from './slices/BannerImage.vue';
import GalleryImage from './slices/GalleryImage.vue';
import Const from '../constants';
import { getCleanPrismicImageUrl } from './helpers/prismicHelpers';
import { translateCategory } from './helpers/newsHelpers';
import { mapSidebarData, loadSidebarCache, saveSidebarCache, CLC_HOME_PAGE_TYPE } from './helpers/sidebarHelpers';

export default {
    name: 'CLCNewsDetail',
    components: {
        SideBar,
        RichText,
        CtaButton,
        ImageWrapper,
        LeyendComponent,
        VideoWrapper,
        QuoteComponent,
        BannerImage,
        GalleryImage
    },
    data() {
        return {
            prismicLang: 'en-us',
            clcName: sessionStorage.getItem('clcName') || '',
            clcSegmentValue: sessionStorage.getItem('clcSegmentValue') || '',
            newsData: null,
            isLoading: true,
            error: null,
            // Branding del rail (highlight + doodle), global desde clc_new_home_page.
            sidebarHighlight: { template: 'empty' },
            dynamicSidebar: { logoSrc: '', bgImage: '' }
        }
    },
    computed: {
        newsTitle() {
            // Usar news_title en lugar de title
            return this.newsData?.data?.news_title?.[0]?.text || 'Untitled';
        },
        // Categoría traducida para el hero (mismo valor que los filtros)
        category() {
            const raw = this.newsData?.data?.categories?.[0]?.category || '';
            return translateCategory(raw, this.prismicLang);
        },
        heroImage() {
            // Usar news_image en lugar de hero_image
            const url = this.newsData?.data?.news_image?.url || '';
            return getCleanPrismicImageUrl(url);
        },
        author() {
            // Author se mantiene igual
            return this.newsData?.data?.author || '';
        },
        publishedDate() {
            // Published date se mantiene igual
            const dateStr = this.newsData?.first_publication_date || '';
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const monthsEN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthsES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            const months = this.prismicLang === 'en-us' ? monthsEN : monthsES;
            return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
        },
        newsBody() {
            // Agregar computed para news_body (si lo necesitas mostrar en algún lugar)
            return this.newsData?.data?.news_body?.[0]?.text || '';
        },
        bodySlices() {
            // Los slices vienen del campo 'body'
            return this.newsData?.data?.body || [];
        },
        homeUrl() {
            const lang = this.prismicLang === 'en-us' ? 'en' : 'es';
            if (this.clcSegmentValue) {
                return `/${lang}/?center=${this.clcSegmentValue}`;
            }
            return `/${lang}/`;
        },
        // Volver al listado de noticias (Etapa 3): el detalle vive bajo /news.
        newsListUrl() {
            const lang = this.prismicLang === 'en-us' ? 'en' : 'es';
            if (this.clcSegmentValue) {
                return `/${lang}/news?center=${this.clcSegmentValue}`;
            }
            return `/${lang}/news`;
        }
    },
    methods: {
        toggleLanguage() {
            // Obtener la última homepage visitada desde sessionStorage
            const lastHomepage = sessionStorage.getItem('lastHomepage');
            const newLang = this.prismicLang === 'en-us' ? 'es' : 'en';
            
            let redirectPath;
            if (!lastHomepage) {
                redirectPath = `/${newLang}/`;
            } else {
                // Separar path y query params
                const [path, query] = lastHomepage.split('?');
                const queryString = query ? `?${query}` : '';
                
                let newPath;
                
                // Si la ruta es la raíz
                if (path === '/') {
                    newPath = `/${newLang}/`;
                } 
                // Si ya tiene idioma, reemplazarlo
                else if (path.match(/^\/(en|es)\//)) {
                    newPath = path.replace(/^\/(en|es)\//, `/${newLang}/`);
                } 
                // Fallback
                else {
                    newPath = `/${newLang}/`;
                }
                
                redirectPath = newPath + queryString;
            }
            
            // Redirigir a la homepage correspondiente
            this.$router.push(redirectPath);
        },

        setLanguage() {
            if (this.$route.path.startsWith('/es')) {
                this.prismicLang = 'es-es';
            } else if (this.$route.path.startsWith('/en')) {
                this.prismicLang = 'en-us';
            }
        },

        getSliceComponent(slice) {
            const sliceType = slice.slice_type;
            
            // Standard slice type mapping
            const sliceMap = {
                'text_block': 'RichText',
                'cta': 'CtaButton',
                'image': 'ImageWrapper',
                'legend_text': 'LeyendComponent',
                'video': 'VideoWrapper',
                'quote': 'QuoteComponent',
                'highlight': 'BannerImage',
                'images_carousel': 'GalleryImage'
            };
            
            return sliceMap[sliceType] || null;
        },

        // Branding global del rail (highlight + doodle) desde el singleton clc_new_home_page.
        // Hidrata el rail desde el cache (evita el flash del fondo default al navegar desde la home).
        hydrateSidebarFromCache() {
            const cached = loadSidebarCache(this.prismicLang);
            if (cached) {
                this.sidebarHighlight = cached.highlight;
                this.dynamicSidebar = cached.dynamicSidebar;
            }
        },
        async getSidebarBranding() {
            try {
                const response = await this.$prismic.client.getSingle(CLC_HOME_PAGE_TYPE, { lang: this.prismicLang });
                const { highlight, dynamicSidebar } = mapSidebarData(response?.data);
                this.sidebarHighlight = highlight;
                this.dynamicSidebar = dynamicSidebar;
                saveSidebarCache(this.prismicLang, { highlight, dynamicSidebar });
            } catch (error) {
                console.error('METHOD: getSidebarBranding ==> ', error);
            }
        },

        async getNewsDetail() {
            try {
                this.isLoading = true;
                this.error = null;

                // Get the news ID from route params
                const newsId = this.$route.params.id;

                if (!newsId) {
                    throw new Error('No news ID provided');
                }

                // Query Prismic for the specific news document by UID
                const response = await this.$prismic.client.getByUID(
                    Const.CLC_NEWS,
                    newsId,
                    {
                        lang: this.prismicLang
                    }
                );

                this.newsData = response;
                console.log('📰 News detail loaded:', this.newsData);

            } catch (error) {
                console.error('Error loading news detail:', error);
                this.error = error.message;
            } finally {
                this.isLoading = false;
                
                // Scroll to content on mobile after data is fully loaded
                this.$nextTick(() => {
                    if (window.innerWidth <= 640) {
                        const target = document.querySelector('.News-detail-anchor');
                        if (target) {
                            setTimeout(() => {
                                const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
                                window.scrollTo({ 
                                    top: targetPosition,
                                    behavior: 'smooth'
                                });
                            }, 500); // Increased delay to ensure all content including images are rendered
                        }
                    }
                });
            }
        }
    },
    async created() {
        this.setLanguage();
        this.hydrateSidebarFromCache();
        this.getSidebarBranding();
        await this.getNewsDetail();
    },
    watch: {
        // Reload data when language changes
        prismicLang() {
            this.getNewsDetail();
            this.getSidebarBranding();
        }
    }
}
</script>